(ns ncr.search
  (:require
   [cljs.core.async :refer-macros [go] :refer [<!]]
   [clojure.core.match :refer [match]]
   [clojure.string :as str]
   [lambdaisland.glogi :as log]
   [uix.core.alpha :as uix]
   [ncr.etc.util :as u]
   [ncr.me :as me]
   [ncr.widgets.core :as w]
   [ncr.etc.pf :as pf]
   [ncr.etc.pubsub :as ps]
   [ncr.etc.reload :as rel]
   [ncr.etc.profile :as p]
   [ncr.etc.profile-pspec :as pspec]
   [ncr.amplitude :as amp]
   [ncr.search.item :as search.item]
   [ncr.search.toolbar :as search.toolbar]
   [ncr.search.bottombar :as search.bottombar]
   [ncr.search.model :as search.model]
   [ncr.fragment-types.attachments :as fragment.attachments]
   [ncr.fragment.props.date :as props-date]
   [ncr.fragment.folder :as fragment-folder]
   [ncr.search.selection :as sel]
   [ncr.widgets.dropzone :as wdz]
   [ncr.widgets.datev]
   [ncr.push :as push]
   [ncr.nsql :as nsql]
   [tick.core :as t]
   [goog.events :as gevents]
   [goog.events.EventType :as EventType]
   [goog.functions :as gfunc]
   [ncr.navigate :as n]))

(defn- extract-matched-fragments [extract-fn separator-fn {:keys [matched rest]}]
  (let
    [{:keys [matched' rest']}
     (reduce
       (fn [acc fragment]
         (if (extract-fn fragment)
           (update acc :matched' conj [:item fragment])
           (update acc :rest' conj fragment)))
       {:matched' []
        :rest' []}
       rest)]
    {:rest rest'
     :matched
     (if-let [separator-text (separator-fn matched')]
       (into (conj matched [:separator separator-text])
         matched')
       matched)}))

(defn- get-date-prop [fragment]
  (get-in fragment [:properties :common/date]))

(defn- extract-period-fragments
  ([start-date end-date separator-text fragments separator-pred]
   (extract-matched-fragments
     (fn [fragment]
       (when-let [d (get-date-prop fragment)]
         (and
           (t/>= d start-date)
           (t/<= d end-date))))
     (fn [matched]
       (when (separator-pred matched)
         (if (fn? separator-text)
           (separator-text start-date end-date matched)
           separator-text)))
     fragments))
  ([start-date end-date separator-text fragments]
   (extract-period-fragments start-date end-date separator-text fragments seq)))

(defn- extract-date-fragments [date separator-text fragments]
  (extract-period-fragments date date separator-text fragments (constantly true)))

(defn- weeks-after-date [date]
  (let
    [get-next-date #(t/>> % (t/new-period 1 :days))
     last-dom (t/last-day-of-month date)
     next-date (min last-dom (get-next-date date))
     first-week-length (- (t/int t/SUNDAY) (-> next-date t/day-of-week t/int))]
    (if (t/= date next-date)
      []
      (loop
        [a next-date
         b (t/min last-dom (t/>> next-date (t/new-period first-week-length :days)))
         weeks [[a b]]]
        (if (t/>= b last-dom)
          (-> weeks reverse vec)
          (let
            [a' (get-next-date b)
             b' (t/min last-dom (t/>> a' (t/new-period 6 :days)))]
            (recur a' b' (conj weeks [a' b']))))))))

(defn- weeks-before-date [date]
  (let
    [get-prev-date #(t/<< % (t/new-period 1 :days))
     first-dom (t/first-day-of-month date)
     prev-date (t/max first-dom (get-prev-date date))
     first-week-length (-> prev-date t/day-of-week t/int (- (t/int t/MONDAY)))]
    (loop
      [b prev-date
       a (t/max first-dom (t/<< prev-date (t/new-period first-week-length :days)))
       weeks [[a b]]]
      (if (t/<= a first-dom)
        weeks
        (let
          [b' (get-prev-date a)
           a' (t/max first-dom (t/<< b' (t/new-period 6 :days)))]
          (recur b' a' (conj weeks [a' b'])))))))

(def ^:private later-week-names
  [["Later this week"]
   ["Next week"]
   ["Next two weeks"]
   ["Next three weeks"]
   ["Next four weeks"]
   ["Next five weeks"]])

(defn- extract-weeks-after-date-fragments [date fragments]
  (let
    [later-week-names
     (if (= t/SUNDAY (t/day-of-week date))
       (rest later-week-names)
       later-week-names)]
    (loop
      [fragments fragments
       weeks (weeks-after-date date)
       week-number (dec (count weeks))]
      (if (empty? weeks)
        fragments
        (recur
          (let [[week-start week-end] (first weeks)]
            (extract-period-fragments week-start week-end
              (nth later-week-names week-number)
              fragments))
          (rest weeks)
          (dec week-number))))))

(def ^:private earlier-week-names
  [["Earlier this week"]
   ["Previous week"]
   ["Previous two weeks"]
   ["Previous three weeks"]
   ["Previous four weeks"]
   ["Previous five weeks"]])

(defn- extract-weeks-before-date-fragments [date fragments]
  (let
    [earlier-week-names
     (if (= t/MONDAY (t/day-of-week date))
       (rest earlier-week-names)
       earlier-week-names)]
    (loop
      [fragments fragments
       weeks (weeks-before-date date)
       week-number 0]
      (if (empty? weeks)
        fragments
        (recur
          (let [[week-start week-end] (first weeks)]
            (extract-period-fragments week-start week-end
              (nth earlier-week-names week-number)
              fragments))
          (rest weeks)
          (inc week-number))))))

(defn- extract-month-fragments [date separator-text fragments]
  (extract-period-fragments (t/first-day-of-month date) (t/last-day-of-month date)
    separator-text
    fragments))

(defn- extract-year-fragments [date separator-text fragments]
  (let
    [year (-> date t/year t/int)
     date-month (-> date t/month t/int)]
    (loop
      [fragments fragments
       month (-> year (t/new-date 12 31) t/month t/int)]
      (if (or (zero? month) (< month date-month))
        fragments
        (recur
          (extract-month-fragments (t/new-date year month 1) separator-text fragments)
          (dec month))))))

(defn- date-to-str [date]
  (let [year (t/year date)]
    [(str
       (when (not= year (t/year (t/today)))
         (str year " / "))
       (-> date t/month str str/capitalize))]))

(defn- extract-months-after-date-fragments [date fragments]
  (let [next-date (t/>> date (t/new-period 1 :days))]
    (loop
      [fragments fragments
       years
       (->> fragments
         :rest
         (filter
           (fn [fragment]
             (when-let [d (get-date-prop fragment)]
               (t/>= d next-date))))
         (map #(-> % get-date-prop t/year t/int))
         (into #{})
         vec
         sort
         reverse)]
      (if (seq years)
        (recur
          (let
            [y (first years)
             d (if (= y (-> date t/year t/int)) next-date (t/new-date (first years) 1 1))]
            (extract-year-fragments d date-to-str fragments))
          (rest years))
        fragments))))

(defn- extract-months-before-date-fragments [date fragments]
  (let [prev-date (t/<< date (t/new-period 1 :days))]
    (loop
      [fragments fragments
       years
       (->> fragments
         :rest
         (filter
           (fn [fragment]
             (when-let [d (get-date-prop fragment)]
               (t/<= d prev-date))))
         (map #(-> % get-date-prop t/year t/int))
         (into #{})
         vec
         sort
         reverse)]
      (if (seq years)
        (recur
          (extract-year-fragments (t/new-date (first years) 1 1) date-to-str fragments)
          (rest years))
        fragments))))

(defn- split-fragments [{:keys [mode no-date-last?]} fragments]
  (case mode
    :none
    (for [fragment fragments]
      [:item fragment])

    :date
    (let
      [today (t/today)
       {:keys [matched rest]}
       (->> {:matched []
             :rest fragments}
         (extract-months-after-date-fragments (t/last-day-of-month today))
         (extract-weeks-after-date-fragments today)
         ((fn [{:keys [matched] :as fragments}]
            (let
              [{matched' :matched :as fragments'}
               (extract-date-fragments today
                 (fn [_ _ matched]
                   [[:<> [:b "Today"] (str ", " (props-date/to-string today))]
                    (if (empty? matched)
                      :ncr-c-search-list__separator_today_empty
                      :ncr-c-search-list__separator_today)])
                 fragments)
               future-fragments? (pos? (-> fragments :matched count))
               today-fragments-count (- (count matched') (count matched) 1)
               today-fragments? (pos? today-fragments-count)]
              (if (or future-fragments? today-fragments?)
                fragments'
                (update fragments' :matched #(-> % drop-last vec))))))
         (extract-weeks-before-date-fragments today)
         (extract-months-before-date-fragments (t/first-day-of-month today)))
       no-date-items
       (if (seq rest)
         (into [[:separator ["No Date"]]] (map #(vector :item %) rest))
         [])]
      (if no-date-last?
        (concat matched no-date-items)
        (concat no-date-items matched)))))

(defn c-search-result-list [{:keys [profile-key]} me fragments selectable? open-in-new-tab?]
  (let [blurred? (pspec/use-search-blur-sensitive)
        group-by (pspec/use-search-group-by)]
    (when-let [group-by (or (get @group-by profile-key) (get @group-by :default))]
      [pf/c-data-list {:is-compact true :style {:border :none}}
       (for [[t p] (split-fragments group-by fragments)]
         (if (= :separator t)
           ^{:key p} [search.item/c-item-date-separator p]
           ^{:key (:id p)}
           [search.item/c-item
            {:me me
             :blurred? @blurred?
             :selectable? selectable?
             :open-in-new-tab? open-in-new-tab?
             :track-origin "Search Item"}
            p]))])))

(defn- c-search-results [{:keys [query-hash] :as props} me fragments]
  (let [id :ncr-c-search-list]
    (uix/with-effect [fragments]
      (when-let [el (.getElementById js/document (name id))]
        (go
          (.scroll el #js {"top" (get (<! (p/read-profile! pspec/search-scroll-top {})) query-hash 0)}))
        (let
          [listener
           (gfunc/debounce
             #(p/swap-profile! pspec/search-scroll-top {} assoc query-hash (.. ^js % getBrowserEvent -srcElement -scrollTop)) 150)]
          (gevents/listen el EventType/SCROLL listener)
          #(gevents/unlisten el EventType/SCROLL listener))))

    [pf/c-page-section
     {:id id
      :class :pf-u-p-0
      :variant :default
      :has-overflow-scroll true
      :is-filled true
      :aria-label "Fragment list"}
     [c-search-result-list props me fragments true false]]))

(defn c-search [{:keys [query] :or {query ""}}]
  (let
    [props
     {:query query
      :query-hash (hash (nsql/parse-simple query))
      :profile-key
      (match (nsql/analyze-simple query)
        {:in folder}
        (str/lower-case folder)
        _
        :default)}]

    [w/c-error-boundary
     (let
       [state
        (rel/use-reload! ::c-search
          (ps/sub-reducer-map! nil
            {::result
             (fn [_ result]
               result)

             :search/execute
             (fn search
               ([_ query-string progress?]
                (go
                  (ps/pub!
                    [::result
                     (u/if-ok [{:keys [fragments] :as result} (<! (search.model/query query-string))]
                       (do
                         (sel/keep!
                           (map :id fragments))
                         [:ok result])
                       (do
                         (sel/clear!)
                         (log/error :error result)
                         [:error result]))]))
                (when progress?
                  :loading))

               ([state progress?]
                (go
                  (ps/pub! [:search/execute query progress?]))
                state))}))]

       (uix/with-effect [query]
         (when (some? query)
           (ps/pub! [:search/execute query true])))

       (push/use-push!
         (fn [message]
           (go
             (match [message @state]
               [{:domain :attachment :event-type :update :domain-id attachment-id} [:ok result]]
               (u/if-ok [result (<! (search.model/reload-attachments result attachment-id))]
                 (ps/pub!
                   [::result [:ok result]])

                 (log/error :error result))
               [_ _]
               nil))))

       (let
         [result @state
          me (uix/context me/*context*)
          folder (fragment-folder/query->folder-id (:folders me) query)
          {:keys [get-root-props is-drag-active]}
          (wdz/use-dropzone
            {:no-click true
             :on-drop-accepted
             (fn [files]
               (go
                 (u/when-ok
                   [_
                    (<!
                      (fragment.attachments/!create-fragment-ui me
                        {:folder folder
                         :files files
                         :name (-> files first (.-path))}))]
                   (ps/pub! [:search/execute false]))))})]
         (match result
           (:or nil :loading)
           [w/c-empty-progress "Loading..."]

           [:ok result']
           [:<>
            [pf/c-page-section {:class :pf-u-p-0 :variant :light :sticky :top :aria-label "Toolbar"}
             [search.toolbar/c-toolbar props (assoc result' :query query)]]

            (let [{:keys [fragments]} result']
              (if-not (empty? fragments)
                [:div {:data-tour-target "search"}
                 [c-search-results props me fragments]
                 [pf/c-page-section {:class :pf-u-p-0 :variant :default :sticky :bottom :style {:flex-grow 0} :aria-label "Totals"}
                  [search.bottombar/c-bottom-bar props result']]]

                [pf/c-page-section
                 {:class [:pf-u-p-0]
                  :variant :light
                  :style {:position :relative}
                  :aria-label "Fragment list (empty)"}
                 [:div.ncr-empty-search
                  (get-root-props)
                  [w/c-dropzone-target (not is-drag-active)]
                  [pf/c-empty-state
                   [pf/c-empty-state-icon
                    {:variant :container :component [w/c-mdi-icon {:icon :feature-search-outline}]}]
                   [pf/c-title {:heading-level :h4 :size :lg} "No Fragments Found"]
                   [pf/c-button
                    {:variant :primary
                     :on-click
                     #(go
                        (u/when-ok [_ (<! (fragment.attachments/!create-fragment-ui me {:folder folder}))]
                          (ps/pub! [:search/execute false])))}
                    "Create Fragment"]

                   [pf/c-empty-state-secondary-actions
                    [pf/c-button
                     {:variant :link
                      :on-click
                      #(do
                         (amp/search! {"Origin" "Empty Search" "Query" ""})
                         (n/push-search! ""))}
                     "Reset Filter"]

                    [pf/c-button
                     {:variant :link
                      :on-click
                      #(ps/pub! [:search/execute true])}
                     "Reload"]]]]]))]

           [:error reason]
           [w/c-empty-error
            (merge {:title "Search Error"}
              (case reason
                "nsql-error"
                {:icon :magnify
                 :body
                 [w/c-empty-error-body {:prefix "Invalid Query"} query]}

                {:body
                 [w/c-empty-error-body reason]}))])))]))

